// The main-thread end of the catalogue worker: request/response over postMessage with progress
// events, typed as CatalogueBackend. The worker is spawned on first use and respawned if it dies.
import {
  queryRequest,
  queryResult,
  type CatalogueBackend,
  type InstalledRef,
  type InstallProgress,
  type SerializedError,
  type WorkerCall,
  type WorkerMessage,
  type WorkerRequest,
} from "./protocol";

/** A failure inside the worker, with the typed cause it reported (see SerializedError). */
export class CatalogueWorkerError extends Error {
  readonly problem: string | undefined;
  readonly failure: string | undefined;
  constructor(error: SerializedError) {
    super(error.message);
    this.name = error.name;
    this.problem = error.problem;
    this.failure = error.failure;
  }
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  onProgress?: (progress: InstallProgress) => void;
}

/** The minimal Worker surface used, so tests can stand in a fake. */
export interface WorkerLike {
  post(message: WorkerRequest): void;
  terminate(): void;
  /** Registers the two handlers, once, right after the worker is spawned. */
  listen(onMessage: (message: WorkerMessage) => void, onError: (message: string) => void): void;
}

function spawnWorker(): WorkerLike {
  const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  return {
    post(message) {
      // A worker's postMessage has no targetOrigin; the lint rule is written for window.postMessage.
      // oxlint-disable-next-line unicorn/require-post-message-target-origin
      worker.postMessage(message);
    },
    terminate: () => {
      worker.terminate();
    },
    listen(onMessage, onError) {
      worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
        onMessage(event.data);
      });
      worker.addEventListener("error", (event) => {
        onError(event.message);
      });
    },
  };
}

export function createWorkerBackend(spawn: () => WorkerLike = spawnWorker): CatalogueBackend {
  let worker: WorkerLike | undefined;
  let nextId = 1;
  const pending = new Map<number, Pending>();

  function fail(reason: Error): void {
    for (const entry of pending.values()) entry.reject(reason);
    pending.clear();
    worker?.terminate();
    worker = undefined;
  }

  function ensureWorker(): WorkerLike {
    if (worker !== undefined) return worker;
    const fresh = spawn();
    fresh.listen(
      (message) => {
        const entry = pending.get(message.id);
        if (entry === undefined) return;
        if (message.kind === "progress") {
          entry.onProgress?.(message.progress);
          return;
        }
        pending.delete(message.id);
        if (message.kind === "result") entry.resolve(message.result);
        else entry.reject(new CatalogueWorkerError(message.error));
      },
      (message) => {
        fail(new Error(message === "" ? "the catalogue worker failed" : message));
      },
    );
    worker = fresh;
    return fresh;
  }

  function send(
    call: WorkerCall,
    onProgress?: (progress: InstallProgress) => void,
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject, ...(onProgress === undefined ? {} : { onProgress }) });
      try {
        ensureWorker().post({ ...call, id });
      } catch (error) {
        pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  return {
    async install(plan, onProgress) {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the worker answers an install with the InstalledRef it stored.
      return (await send({ op: "install", plan }, onProgress)) as InstalledRef;
    },
    async applyDelta(plan, onProgress) {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the worker answers with the InstalledRef it stored.
      return (await send({ op: "applyDelta", plan }, onProgress)) as InstalledRef;
    },
    async open(ref) {
      return queryResult("meta", await send({ op: "open", ref }));
    },
    async prune(keep) {
      await send({ op: "prune", keep });
    },
    async query(name, ...args) {
      return queryResult(name, await send({ op: "query", query: queryRequest(name, args) }));
    },
  };
}
